import { CheckIcon, ClipboardDocumentIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/20/solid';
import React from 'react';

import { AnimatePresence, ScaleSwap } from './Animations';
import { cn } from './cn';

export interface TextWithCopyProps {
  text: string;
  /**
   * Shortened form to render in place of `text` — e.g. a middle-truncated UUID. Copying and the `title` tooltip
   * always use the full `text`.
   */
  displayText?: string;
  className?: string;
  revealable?: boolean;
  /** Suppress the native `title` attribute — e.g. when the caller wraps this in a styled Tooltip. */
  noTitle?: boolean;
}

export const TextWithCopy = ({ text, displayText = text, className, revealable, noTitle }: TextWithCopyProps) => {
  const [copied, setCopied] = React.useState(false);
  const [revealed, setRevealed] = React.useState(!revealable);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <span className={cn('inline-flex h-7 items-center gap-1.5 text-sm min-w-0', className)}>
      {/* `grow` pins the trailing buttons to the right edge when the caller fixes the
          overall width; in content-sized usages there is no free space, so it's a no-op. */}
      <span
        className="font-mono text-xs text-gray-600 min-w-0 grid grow"
        title={revealed && !noTitle ? text : undefined}
      >
        {/* Both layers share one grid cell so the wider one reserves the width and
            toggling reveal never shifts the trailing buttons. While masked, the
            text must stay out of the DOM (it may be a secret extractable from the
            markup), so an empty spacer reserves its width instead \u2014 `ch` is exact
            here because the font is monospace. The mask is always exactly eight
            bullets regardless of text length. */}
        {revealed ? (
          <span className="col-start-1 row-start-1 truncate">{displayText}</span>
        ) : (
          <span aria-hidden className="col-start-1 row-start-1 truncate" style={{ width: `${displayText.length}ch` }} />
        )}
        <span aria-hidden={revealed} className={cn('col-start-1 row-start-1 truncate', revealed && 'invisible')}>
          {'\u2022'.repeat(8)}
        </span>
      </span>
      {revealable && (
        <button
          onClick={() => setRevealed(!revealed)}
          className="inline-flex items-center shrink-0 p-0.5 rounded-md text-gray-400 hover:text-gray-600 transition-all duration-200 cursor-pointer bg-transparent border-none"
          title={revealed ? 'Hide' : 'Reveal'}
        >
          {revealed ? <EyeSlashIcon className="h-3.5 w-3.5" /> : <EyeIcon className="h-3.5 w-3.5" />}
        </button>
      )}
      <button
        onClick={handleCopy}
        className="inline-flex items-center shrink-0 p-0.5 rounded-md text-gray-400 hover:text-gray-600 transition-all duration-200 cursor-pointer bg-transparent border-none"
        title="Copy to clipboard"
      >
        <AnimatePresence mode="wait">
          {copied ? (
            <ScaleSwap key="check">
              <CheckIcon className="h-3.5 w-3.5 text-success-500" />
            </ScaleSwap>
          ) : (
            <ScaleSwap key="copy">
              <ClipboardDocumentIcon className="h-3.5 w-3.5" />
            </ScaleSwap>
          )}
        </AnimatePresence>
      </button>
    </span>
  );
};
